import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Tag } from "lucide-react";

const EMPTY = {
  code: "",
  discount_type: "percentage",
  value: 10,
  min_order: 0,
  max_uses: "",
  expires_at: "",
  first_order_only: false,
  active: true,
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data);
    } catch (err) {
      toast.error("Failed to load coupons");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: Number(form.value),
        min_order: Number(form.min_order || 0),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this coupon?")) return;
    try {
      await api.delete(`/admin/coupons/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="admin-coupons" className="mt-8">
      <button
        onClick={() => setOpen(true)}
        data-testid="add-coupon-btn"
        className="inline-flex items-center gap-2 bg-[#274e13] hover:bg-[#38761d] text-white px-4 py-2 rounded-sm text-sm font-semibold"
      >
        <Plus size={14} /> Add coupon
      </button>

      <div className="mt-6 bg-white border border-stone-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-100">
            <tr className="text-left text-xs tracking-wider uppercase text-stone-600">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Discount</th>
              <th className="px-4 py-3">Min order</th>
              <th className="px-4 py-3">Uses</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-stone-500">No coupons yet. Click "Add coupon" to create one.</td></tr>
            ) : coupons.map((c) => (
              <tr key={c.id} className="border-t border-stone-100" data-testid={`coupon-row-${c.id}`}>
                <td className="px-4 py-3 font-mono font-semibold inline-flex items-center gap-2"><Tag size={12} className="text-[#b45f06]" />{c.code}</td>
                <td className="px-4 py-3">{c.discount_type === "percentage" ? `${c.value}% off` : `₹${c.value} off`}</td>
                <td className="px-4 py-3 text-stone-600">{c.min_order > 0 ? `₹${c.min_order}` : "—"}</td>
                <td className="px-4 py-3">{c.uses}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                <td className="px-4 py-3 text-stone-600">{c.expires_at || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`coupon-delete-${c.id}`} onClick={() => remove(c.id)} className="text-red-700 hover:bg-red-50 px-2 py-1 rounded-sm"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4">
          <form onSubmit={save} className="bg-white rounded-sm w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold mb-4">New coupon</h2>
            <div className="grid grid-cols-2 gap-3">
              <CField label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} required testId="coup-code" placeholder="WELCOME10" />
              <div>
                <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">Discount type</label>
                <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2 text-sm" data-testid="coup-type">
                  <option value="percentage">% Percentage</option>
                  <option value="fixed">₹ Fixed amount</option>
                </select>
              </div>
              <CField label="Value" type="number" value={form.value} onChange={(v) => setForm({ ...form, value: v })} required testId="coup-value" />
              <CField label="Min order ₹" type="number" value={form.min_order} onChange={(v) => setForm({ ...form, min_order: v })} testId="coup-min" />
              <CField label="Max uses (blank = ∞)" type="number" value={form.max_uses} onChange={(v) => setForm({ ...form, max_uses: v })} testId="coup-maxuses" />
              <CField label="Expires (YYYY-MM-DD)" type="date" value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} testId="coup-expires" />
              <label className="col-span-2 flex items-center gap-2 text-sm p-3 bg-stone-50 border border-stone-200 rounded-sm">
                <input type="checkbox" checked={form.first_order_only} onChange={(e) => setForm({ ...form, first_order_only: e.target.checked })} data-testid="coup-firstonly" />
                <span><b>First order only</b> <span className="text-stone-500 text-xs ml-1">(only for registered customers' first purchase)</span></span>
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-stone-300 rounded-sm">Cancel</button>
              <button data-testid="coup-save" type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#274e13] text-white rounded-sm font-semibold">{saving ? "Saving..." : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function CField({ label, testId, ...rest }) {
  return (
    <div>
      <label className="text-xs tracking-wider uppercase text-stone-500 font-semibold">{label}</label>
      <input data-testid={testId} {...rest} className="mt-1 w-full bg-[#fafaf9] border border-stone-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#274e13]" />
    </div>
  );
}
